import * as React from "react"
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"

// ─── Context ──────────────────────────────────────────────────────────────────

interface ComboboxContextValue {
  /** The chips field, when one is rendered — the popover's anchor. */
  anchorRef: React.MutableRefObject<HTMLDivElement | null>
  open: boolean
  setOpen: (open: boolean) => void
  inputValue: string
  setInputValue: (v: string) => void
  multiple: boolean
  disabled: boolean
  // single-select
  value?: string
  onValueChange?: (value: string) => void
  // multi-select
  values?: string[]
  onValuesChange?: (values: string[]) => void
}

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null)

function useCombobox() {
  const ctx = React.useContext(ComboboxContext)
  if (!ctx) throw new Error("Combobox components must be used inside <Combobox>")
  return ctx
}

/** Read the combobox state from a custom child — e.g. to filter items. */
function useComboboxContext() {
  return useCombobox()
}

// ─── Root ─────────────────────────────────────────────────────────────────────

interface ComboboxSingleProps {
  multiple?: false
  value?: string
  onValueChange?: (value: string) => void
  values?: never
  onValuesChange?: never
}

interface ComboboxMultipleProps {
  multiple: true
  values?: string[]
  onValuesChange?: (values: string[]) => void
  value?: never
  onValueChange?: never
}

type ComboboxProps = (ComboboxSingleProps | ComboboxMultipleProps) & {
  disabled?: boolean
  children: React.ReactNode
  className?: string
}

function Combobox({
  children,
  multiple = false,
  disabled = false,
  value,
  onValueChange,
  values,
  onValuesChange,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const anchorRef = React.useRef<HTMLDivElement | null>(null)

  return (
    <ComboboxContext.Provider
      value={{
        anchorRef,
        open,
        setOpen,
        inputValue,
        setInputValue,
        multiple: !!multiple,
        disabled,
        value,
        onValueChange,
        values,
        onValuesChange,
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </ComboboxContext.Provider>
  )
}

// ─── Trigger ──────────────────────────────────────────────────────────────────

function ComboboxTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof PopoverTrigger>) {
  return (
    <PopoverTrigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground" />
    </PopoverTrigger>
  )
}

// ─── Content ──────────────────────────────────────────────────────────────────

function ComboboxContent({
  className,
  children,
  sideOffset = 6,
  align = "start",
  // anchor is a no-op in the Radix version — PopoverContent anchors to the
  // trigger, or to the chips field via PopoverAnchor
  anchor: _anchor,
  onPointerDownOutside,
  onFocusOutside,
  ...props
}: React.ComponentProps<typeof PopoverContent> & { anchor?: unknown }) {
  const { anchorRef } = useCombobox()
  // The chips field lives outside the content; clicking it, or focusing its
  // input, must not close the list, or its own onClick would immediately
  // reopen it — a visible flicker.
  const inAnchor = (event: Event) => !!anchorRef.current?.contains(event.target as Node)
  return (
    <PopoverContent
      data-slot="combobox-content"
      sideOffset={sideOffset}
      align={align}
      onPointerDownOutside={(event) => {
        if (inAnchor(event)) {
          event.preventDefault()
          return
        }
        onPointerDownOutside?.(event)
      }}
      onFocusOutside={(event) => {
        if (inAnchor(event)) {
          event.preventDefault()
          return
        }
        onFocusOutside?.(event)
      }}
      className={cn("w-(--radix-popover-trigger-width) p-0", className)}
      {...props}
    >
      <Command>{children}</Command>
    </PopoverContent>
  )
}

// ─── Input ────────────────────────────────────────────────────────────────────

function ComboboxInput({
  className,
  placeholder,
  onValueChange,
  ...props
}: React.ComponentProps<typeof CommandInput>) {
  const { inputValue, setInputValue } = useCombobox()
  return (
    <CommandInput
      data-slot="combobox-input"
      value={inputValue}
      // The combobox owns the typed text; a caller searching the server is told as well.
      onValueChange={(v) => {
        setInputValue(v)
        onValueChange?.(v)
      }}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  )
}

// ─── List ─────────────────────────────────────────────────────────────────────

function ComboboxList({ className, ...props }: React.ComponentProps<typeof CommandList>) {
  return (
    <CommandList
      data-slot="combobox-list"
      className={cn("max-h-72", className)}
      {...props}
    />
  )
}

// ─── Empty ────────────────────────────────────────────────────────────────────

function ComboboxEmpty({ className, ...props }: React.ComponentProps<typeof CommandEmpty>) {
  return (
    <CommandEmpty
      data-slot="combobox-empty"
      className={cn("py-2 text-center text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

// ─── Group ────────────────────────────────────────────────────────────────────

function ComboboxGroup({ className, ...props }: React.ComponentProps<typeof CommandGroup>) {
  return <CommandGroup data-slot="combobox-group" className={className} {...props} />
}

// ─── Label ────────────────────────────────────────────────────────────────────

function ComboboxLabel({ className, children, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="combobox-label"
      className={cn("px-2 py-1.5 text-xs text-muted-foreground", className)}
      {...props}
    >
      {children}
    </div>
  )
}

// ─── Item ─────────────────────────────────────────────────────────────────────

function ComboboxItem({
  className,
  children,
  value,
  ...props
}: React.ComponentProps<typeof CommandItem> & { value: string }) {
  const { multiple, onValueChange, values, onValuesChange, setOpen, setInputValue } =
    useCombobox()

  const isSelected = multiple ? (values ?? []).includes(value) : false

  const handleSelect = () => {
    if (multiple) {
      const current = values ?? []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      onValuesChange?.(next)
    } else {
      onValueChange?.(value)
      setOpen(false)
      setInputValue("")
    }
  }

  return (
    <CommandItem
      data-slot="combobox-item"
      value={value}
      onSelect={handleSelect}
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      {multiple && (
        <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
          {isSelected && <CheckIcon className="size-4" />}
        </span>
      )}
    </CommandItem>
  )
}

// ─── Separator ────────────────────────────────────────────────────────────────

function ComboboxSeparator({ className, ...props }: React.ComponentProps<typeof CommandSeparator>) {
  return (
    <CommandSeparator
      data-slot="combobox-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  )
}

// ─── Collection (no-op shim for API compat) ───────────────────────────────────

function ComboboxCollection({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

// ─── Value (no-op shim) ───────────────────────────────────────────────────────

function ComboboxValue({ children }: { children?: React.ReactNode }) {
  return <>{children}</>
}

// ─── Chips (multi-select chip input) ─────────────────────────────────────────

interface ComboboxChipsProps extends React.ComponentProps<"div"> {
  children: React.ReactNode
}

const ComboboxChips = React.forwardRef<HTMLDivElement, ComboboxChipsProps>(
  ({ className, children, ...props }, ref) => {
    const { setOpen, disabled, anchorRef } = useCombobox()
    return (
      <PopoverAnchor asChild>
        <div
          ref={(node) => {
            anchorRef.current = node
            if (typeof ref === "function") ref(node)
            else if (ref) ref.current = node
          }}
          data-slot="combobox-chips"
          onClick={(event) => {
            if (disabled) return
            setOpen(true)
            // Keep typing (and filtering) working after a click on the chips.
            findChipsInput(event.currentTarget)?.focus()
          }}
          className={cn(
            "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 cursor-text",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </PopoverAnchor>
    )
  }
)
ComboboxChips.displayName = "ComboboxChips"

function findChipsInput(container: HTMLElement) {
  return container.querySelector<HTMLInputElement>('[data-slot="combobox-chip-input"]')
}

// ─── Chip ─────────────────────────────────────────────────────────────────────

interface ComboboxChipProps extends React.ComponentProps<"span"> {
  children: React.ReactNode
  showRemove?: boolean
  onRemove?: () => void
}

function ComboboxChip({ className, children, showRemove = true, onRemove, ...props }: ComboboxChipProps) {
  return (
    <span
      data-slot="combobox-chip"
      className={cn(
        "flex h-5.5 w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground",
        className
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-ml-1 size-4 opacity-50 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation()
            onRemove?.()
          }}
          data-slot="combobox-chip-remove"
        >
          <XIcon className="size-3" />
        </Button>
      )}
    </span>
  )
}

// ─── ChipsInput ───────────────────────────────────────────────────────────────

function ComboboxChipsInput({ className, placeholder, ...props }: React.ComponentProps<"input">) {
  const { inputValue, setInputValue, setOpen } = useCombobox()
  return (
    <input
      data-slot="combobox-chip-input"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onFocus={() => setOpen(true)}
      placeholder={placeholder}
      className={cn("min-w-16 flex-1 bg-transparent outline-none text-sm", className)}
      {...props}
    />
  )
}

// ─── Anchor hook (no-op shim — PopoverContent auto-anchors) ──────────────────

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null)
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
  useComboboxContext,
}
